import { useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { Routes, Route, useLocation } from 'react-router-dom';

import './App.css';

import Health from './components/Admin/Health';
import LoginController from './components/Auth/LoginController';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import CaseReportServiceFactory from './services/CaseReportServiceFactory';
import configureAmplify from './utils/amplifyConfig';
import { AppConfig, getAppConfig } from './utils/appConfig';
import { initializeAwsTracking } from './utils/awsTracking';
import { initGA } from './utils/ga4Config';
import { logPageView } from './utils/trackingUtils';

import { ContactUs } from './pages/ContactUs';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { RootLayout } from './components/layout/RootLayout';
import { CaseLibraryPage } from './pages/CaseLibraryPage';
import { CaseReportPage } from './pages/CaseReport/CaseReportPage';
import PortalLayout from './components/layout/PortalLayout';

const caseReportServiceFactory = new CaseReportServiceFactory();
const config: AppConfig = getAppConfig();
const appName = config.ENV.toUpperCase() === 'PROD' ?
  'Proprio Data Apps' :
  `Proprio Data Apps (${config.ENV})`;

// Amplify is used for the authorization flow.
configureAmplify();

initializeAwsTracking(config);

// Initialize Google Analytics to track page views and user interactions.
initGA(config.GA4_TRACKING_ID);

// Track page views using this wrapper.
const PageAnalyticsTracker = ({ children }: { children: React.JSX.Element }): React.JSX.Element => {
  const location = useLocation();

  useEffect(() => {
    logPageView(location.pathname);
  }, [location]);

  return children;
};

const caseReportService = caseReportServiceFactory.create(config.ENV);

// Create a wrapper component for the case detail route
const CaseDetailRoute = (): React.JSX.Element => {
  const { caseName } = useParams<{ caseName: string }>();
  const navigate = useNavigate();

  if (!caseName) {
    return <Navigate to="/" replace />;
  }

  return (
    <CaseReportPage
      caseName={caseName}
      caseReportService={caseReportService}
      onNavigate={navigate}
    />
  );
};

function App(): React.JSX.Element {
  const navigate = useNavigate();

  const handleAuthSuccess = async (redirectTo: string): Promise<void> => {
    navigate(redirectTo || '/');
  };

  return (
    <PageAnalyticsTracker>
      <Routes>
        <Route element={<RootLayout />}>
          <Route path="/" element={
            <ProtectedRoute>
              <PortalLayout>
                <CaseLibraryPage
                  env={config.ENV || ''}
                  onNavigate={(path: string)=> {navigate(path);}}
                />
              </PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/login" element={
            <LoginController
              appName={appName}
              onAuthSuccess={handleAuthSuccess}
            />
          } />
          <Route path="/health" element={<Health />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/cases/:caseName"
            element={
              <ProtectedRoute>
                <PortalLayout>
                  <CaseDetailRoute />
                </PortalLayout>
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </PageAnalyticsTracker>
  );
}

export default App;
